import React, { useState, useEffect, useCallback, useRef } from "react";
import SelectContributor from "../components/SelectContributor";
import {
  postTransaction,
  putTransaction,
  postTransactionReceipt,
  getTransactionReceiptUrl,
} from "../services/AnimalSplitCore";
import {
  amountInputToData,
  amountDataToDisplay,
  toggleModal,
  optimizeImageToPngIfOverBytes,
} from "../utils/utils";
import SelectTransactionType from "../components/SelectTransactionType";
import ExactTransactionContributorList from "./ExactTransactionContributorList";
import ShareTransactionContributorList from "./ShareTransactionContributorList";
import "./EditTransaction.css";
import "./EditTransactionTheme.css";
import "./GroupPageCards.css";
import { TRANSACTION_CATEGORIES, MODAL_ID } from "../constants";
import { useTranslation } from "react-i18next";
import InfoModal from "../components/InfoModal";
import AnimalEasterEggBadge from "../components/AnimalEasterEggBadge";

export default function EditTransaction(props) {
  const { t } = useTranslation();
  const shareType = "share";
  const exactType = "exact";

  const {
    newTransaction,
    transaction,
    modalId,
    groupId,
    contributors,
    renderGroup,
    setIsLoading,
    isSettled = false,
  } = props;
  const hasReceipt = !newTransaction && !!transaction?.receiptKey;
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [contributorId, setContributorId] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [contributorAmountList, setContributorAmountList] = useState({});
  const [contributorAmountIsEditing, setContributorAmountIsEditing] =
    useState(false);
  const [contributorShareList, setContributorShareList] = useState({});
  const [formValidated, setFormValidated] = useState(false);
  const [isContributionInvalid, setIsContributionInvalid] = useState(false);
  const [shouldReloadRow, setShouldReloadRow] = useState(0);
  const [amountSyncVersion, setAmountSyncVersion] = useState(0);

  const [resetShare, setResetShare] = useState(false);
  const [editMode, setEditMode] = useState('');
  const receiptSectionVisible = editMode || hasReceipt;
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const receiptInputRef = useRef(null);
  const receiptInfoBtnId = `${modalId}-receipt-info-btn`;
  const receiptInfoModalId = `${modalId}-receipt-info-modal`;
  const receiptModalId = `${modalId}-receipt-preview`;
  const modalTitle = newTransaction
    ? t("transaction.create_transaction")
    : editMode
      ? t("transaction.edit_transaction")
      : t("transaction.title");

  const MAX_RECEIPT_BYTES = 50 * 1024 * 1024; // keep in sync with nginx/multer
  const MAX_RECEIPT_MB = Math.round(MAX_RECEIPT_BYTES / (1024 * 1024));
  const RECEIPT_OPTIMIZE_THRESHOLD_BYTES = 1 * 1024 * 1024; // 1MB

  const buildDefaultAffected = useCallback(
    (list) => {
      const out = {};
      (Array.isArray(list) ? list : []).forEach((c) => {
        if (!c?.id) return;
        out[c.id] = { share: 1, amount: 0 };
      });
      return out;
    },
    []
  );

  const validateForm = useCallback(() => {
    // Validate presence of title and positive numeric amount
    const parsed = parseFloat(amount);
    const amountValid = !isNaN(parsed) && parsed > 0;
    const formInvalid = title.trim() === "" || !amountValid;

    let contributionInvalid = false;
    if (type === exactType) {
      const contributorValues = Object.values(contributorAmountList);
      const contributorTotal = contributorValues.reduce(
        (acc, c) => acc + (c.amount || 0),
        0
      );
      // Only check total if amount is a valid number
      if (amountValid && contributorTotal !== amountInputToData(amount)) {
        contributionInvalid = true;
      }
    } else {
      const shareValues = Object.values(contributorShareList);
      const shareTotal = shareValues.reduce((acc, c) => {
        if (c.share < 0) contributionInvalid = true;
        return acc + (c.share || 0);
      }, 0);
      if (shareTotal === 0) contributionInvalid = true;
    }

    return { formInvalid, contributionInvalid };
  }, [amount, title, contributorAmountList, contributorShareList, type]);

  const handleModalWillShow = useCallback(() => {
    if (newTransaction) {
      const defaultContributorId = (Array.isArray(contributors) ? contributors : [])?.[0]?.id || "";
      setTitle("");
      setAmount("");
      setContributorId(defaultContributorId);
      setCategory("other");
      // Default to shares so the UI is ready immediately.
      setType(shareType);
      // Pre-seed both lists so save validation doesn't depend on child row mount timing.
      const defaults = buildDefaultAffected(contributors);
      setContributorAmountList(defaults);
      setContributorAmountIsEditing(false);
      setContributorShareList(defaults);
      setFormValidated(false);
      setIsContributionInvalid(false);
      setEditMode(true);
      setReceiptFile(null);
      setReceiptPreviewUrl(null);
      setAmountSyncVersion(0);
    } else {
      setTitle(transaction.title);
      setAmount(`${amountDataToDisplay(transaction.amount)}`);
      setContributorId(transaction.contributorId);
      setCategory(transaction.category);
      setType(transaction.type);
      setContributorAmountIsEditing(true);
      setFormValidated(false);
      setIsContributionInvalid(false);
      setShouldReloadRow((prev) => prev + 1);
      if (transaction.type === shareType) {
        setContributorAmountList({});
        setContributorShareList(transaction.affectedContributors);
      } else if (transaction.type === exactType) {
        setContributorAmountList(transaction.affectedContributors);
        setContributorShareList({});
      }
      // If not settled, open directly in edit mode; otherwise view mode
      setEditMode(!isSettled);
      setReceiptFile(null);
      setReceiptPreviewUrl(null);
      setAmountSyncVersion(0);
    }
  }, [newTransaction, transaction, contributors, buildDefaultAffected, shareType, exactType, isSettled]);

  // Settled groups are read-only: never allow entering edit mode.
  useEffect(() => {
    if (isSettled) setEditMode(false);
  }, [isSettled]);

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
  
    if (!modalElement) return;
  
    // Bootstrap modal event binding
    const handleShow = () => {
      handleModalWillShow();
    };
  
    // Call once initially (if needed outside modal lifecycle)
    handleModalWillShow();
  
    // Add listener
    modalElement.addEventListener("show.bs.modal", handleShow);
  
    // Cleanup
    return () => {
      modalElement.removeEventListener("show.bs.modal", handleShow);
    };
  }, [modalId, handleModalWillShow]);

  // If contributors load after the modal initializes, fill missing defaults (without overriding user edits).
  useEffect(() => {
    if (!newTransaction) return;
    const list = Array.isArray(contributors) ? contributors : [];
    if (list.length === 0) return;

    if (!contributorId) {
      setContributorId(list[0]?.id || "");
    }

    // If the affected list is empty (or missing any newly added contributor), seed it.
    setContributorShareList((prev) => {
      const prevKeys = prev ? Object.keys(prev) : [];
      const next = { ...(prev || {}) };
      let changed = false;
      list.forEach((c) => {
        if (!c?.id) return;
        if (!next[c.id]) {
          next[c.id] = { share: 1, amount: 0 };
          changed = true;
        }
      });
      return changed || prevKeys.length === 0 ? next : prev;
    });

    setContributorAmountList((prev) => {
      const prevKeys = prev ? Object.keys(prev) : [];
      const next = { ...(prev || {}) };
      let changed = false;
      list.forEach((c) => {
        if (!c?.id) return;
        if (!next[c.id]) {
          next[c.id] = { share: 1, amount: 0 };
          changed = true;
        }
      });
      return changed || prevKeys.length === 0 ? next : prev;
    });
  }, [newTransaction, contributors, contributorId]);
  
  const handleSetCategory = (newCategory) => {
    if (t("transaction.category." + category) === title || title === "") {
      const categoryTitle = newCategory.replace(category[0], category[0]);
      setTitle(t("transaction.category." + categoryTitle));
    }
    setCategory(newCategory);
  };

  const splitEvenly = () => {
    switch (type) {
      case exactType:
        setContributorAmountIsEditing(false);
        setAmount(amount);
        break;
      default:
        setResetShare(true);
        break;
    }
  };

  const handleSave = async () => {
    const { formInvalid, contributionInvalid } = validateForm();
    if (formInvalid) {
      setFormValidated(true);
      // show validation modal with a helpful message
      let msg = t("transaction.validation.missing_both");
      if (title.trim() === "") msg = t("transaction.validation.missing_description");
      else if (amount === "" || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)
        msg = t("transaction.validation.missing_amount");

      toggleModal(MODAL_ID.VALIDATION_ERROR, {
        title: t("transaction.validation.invalid_input_title"),
        message: msg,
      });

      return;
    }

    if (contributionInvalid) {
      setFormValidated(true);
      setIsContributionInvalid(true);
      if (type === exactType) {
        toggleModal(MODAL_ID.EXACT_MISMATCH, {
          title: t("transaction.validation.amounts_dont_match_title"),
          message: t("transaction.validation.amounts_dont_match_message"),
        });
      } else {
        toggleModal(MODAL_ID.VALIDATION_ERROR, {
          title: t("transaction.validation.invalid_share_values_title"),
          message: t("transaction.validation.invalid_share_values_message"),
        });
      }

      return;
    }
    setIsLoading?.(true);
    if (newTransaction) {
      const affectedContributors =
        type === exactType ? contributorAmountList : contributorShareList;

      // Populate default data for non selected transaction type
      if (type === exactType) {
        Object.values(affectedContributors).forEach((c) => {
          c.share = 1;
        });
      } else {
        const contribValues = Object.values(affectedContributors);
        let averageAmount = Math.round(
          amountInputToData(amount) / contribValues.length
        );
        let accu_sum = 0;
        contribValues.forEach((c, i, arr) => {
          let contributorAmount = averageAmount;
          if (i === arr.length - 1)
            contributorAmount = amountInputToData(amount) - accu_sum;
          accu_sum += contributorAmount;
          c.amount = contributorAmount;
        });
      }

      try {

        const { payload } = await postTransaction(groupId, {
          title,
          amount: amountInputToData(amount),
          contributorId,
          type,
          category,
          affectedContributors,
        });
        renderGroup(payload);

        const transactionId = payload.transactions[0].id;

        if (receiptFile) {
          const fd = new FormData();
          fd.append("receipt", receiptFile);
          const { payload } = await postTransactionReceipt(groupId, transactionId, fd);
          renderGroup(payload);
          setReceiptFile(null);
        }

        setIsLoading?.(false);
        setEditMode(false);
      } catch (error) {
        setIsLoading?.(false);
        console.error(error);
        toggleModal(MODAL_ID.CONNECTION_ERROR);
      }
    } else {
      const { id: transactionId } = transaction;
      const affectedContributors =
        type === exactType ? contributorAmountList : contributorShareList;

      // Additional guard when editing: ensure exact amounts sum matches total
      if (type === exactType) {
        const contributorTotal = Object.values(affectedContributors).reduce(
          (acc, c) => acc + (c.amount || 0),
          0
        );
        if (contributorTotal !== amountInputToData(amount)) {
          setFormValidated(true);
          setIsContributionInvalid(true);
          setIsLoading?.(false);
          toggleModal(MODAL_ID.EXACT_MISMATCH);
          return;
        }
      }

      // Inject non-selected type value
      const nonSelectedType = type === exactType ? shareType : "amount";
      Object.entries(affectedContributors).forEach(([k, v]) => {
        if (!transaction.affectedContributors[k]) return;
        v[nonSelectedType] = transaction.affectedContributors[k][
          nonSelectedType
        ];
      });

      try {
        const { payload } = await putTransaction(groupId, transactionId, {
          amount: amountInputToData(amount),
          title,
          type,
          contributorId,
          category,
          affectedContributors
        });
        setIsLoading?.(false);
        renderGroup(payload);
        setEditMode(false);

        if (receiptFile) {
          setIsLoading?.(true);
          const fd = new FormData();
          fd.append("receipt", receiptFile);
          const { payload } = await postTransactionReceipt(groupId, transactionId, fd);
          renderGroup(payload);
          setReceiptFile(null);
          setIsLoading?.(false);
        }
        
      } catch (error) {
        setIsLoading?.(false);
        console.error(error);
        toggleModal(MODAL_ID.CONNECTION_ERROR);
      }
    }
  };

  const handleViewReceipt = async () => {
    try {
      if (newTransaction || !transaction?.id) return;
      setIsLoading?.(true);
      const { payload } = await getTransactionReceiptUrl(groupId, transaction.id);
      setIsLoading?.(false);
      if (payload?.url) {
        setReceiptPreviewUrl(payload.url);
        // Bootstrap doesn't stack modals reliably; close the transaction modal first.
        const txModalEl = document.getElementById(modalId);
        if (txModalEl) {
          const inst = window.bootstrap.Modal.getInstance(txModalEl);
          (inst ?? new window.bootstrap.Modal(txModalEl)).hide();
        }

        // Then open the receipt modal (slight delay so backdrops/z-index settle).
        window.setTimeout(() => {
          const receiptEl = document.getElementById(receiptModalId);
          if (!receiptEl) return;

          // When receipt modal closes, reopen the transaction modal (view mode)
          // to keep context and avoid focus issues with hidden ancestors.
          receiptEl.addEventListener(
            "hidden.bs.modal",
            () => {
              setReceiptPreviewUrl(null);
              const txEl = document.getElementById(modalId);
              if (txEl) new window.bootstrap.Modal(txEl).show();
            },
            { once: true }
          );

          new window.bootstrap.Modal(receiptEl).show();
        }, 250);
      }
    } catch (error) {
      setIsLoading?.(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };



  return (
    <>
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content as-transaction-modal as-theme">
          <div className="modal-header">
            <div className="as-transaction-modal-header">
              <div className="as-transaction-modal-title">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  🧾
                </span>
                <h5 className="modal-title" id="exampleModalLabel">
                  {modalTitle}
                </h5>
              </div>

              <div className="as-transaction-modal-actions">
                <AnimalEasterEggBadge seed={groupId} />
                {!newTransaction && !editMode && !isSettled && (
                  <button
                    type="button"
                    className="btn btn-sm as-btn-soft"
                    onClick={() => setEditMode(true)}
                    aria-label="Edit"
                  >
                    <i className="bi bi-pencil-fill" />
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
            </div>
          </div>

          <div className={`modal-body ${formValidated ? "was-validated" : ""}`}>
            <div className="col">
              <div className="d-flex flex-wrap">
                {Object.entries(TRANSACTION_CATEGORIES).map(([key, value]) => (
                  <div className="d-inline-block mb-1 mx-1" key={key}>
                    <div className="d-flex flex-column justify-items-center">
                      <button
                        type="button"
                        id={key}
                        className={`btn ${
                          category === key
                            ? "btn-outline-warning"
                            : "btn-outline-light"
                        } btn-sm as-category-btn`}
                        onClick={() => handleSetCategory(key)}
                        disabled={!editMode}
                      >
                        <h3 className="m-0">{value}</h3>
                      </button>
                      <p className="text-center text-wrap category-label">
                        {t(`transaction.category.${key}`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label as-field-label">
                <span className="as-field-label-badge" aria-hidden="true">📝</span>
                <span className="as-field-label-text">{t("transaction.description")}</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={title ?? ""}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={!editMode}
              />
            </div>
            <div className="mb-3 row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label as-field-label">
                  <span className="as-field-label-badge" aria-hidden="true">💰</span>
                  <span className="as-field-label-text">{t("transaction.amount")}</span>
                </label>
                <div className="as-tx-control">
                  <input
                    type="number"
                    className="form-control"
                    value={amount ?? ""}
                    onChange={(e) => {
                      setContributorAmountIsEditing(false);
                      setAmount(e.target.value);
                    }}
                    required
                    disabled={!editMode}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label as-field-label">
                  <span className="as-field-label-badge" aria-hidden="true">👤</span>
                  <span className="as-field-label-text">{t("transaction.contributor")}</span>
                </label>
                <div className="as-tx-control">
                  <SelectContributor
                    contributors={contributors}
                    contributorId={contributorId}
                    setContributorId={setContributorId}
                    editMode={editMode}
                  />
                </div>
              </div>
            </div>

            {receiptSectionVisible && (
              <div className="mb-3 row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label as-field-label">
                    <span className="as-field-label-badge" aria-hidden="true">🧾</span>
                    <span className="as-field-label-text">{t("transaction.receipt.label")}</span>
                    <button
                      id={receiptInfoBtnId}
                      type="button"
                      className="btn btn-sm as-info-icon-btn"
                      aria-label={t("transaction.receipt.privacy_info_aria")}
                      data-bs-toggle="modal"
                      data-bs-target={`#${receiptInfoModalId}`}
                    >
                      <span aria-hidden="true">ℹ️</span>
                    </button>
                  </label>

                  <div className="as-tx-control">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {hasReceipt && (
                        <button
                          type="button"
                          className="btn btn-sm as-btn-soft"
                          onClick={handleViewReceipt}
                        >
                          👀 {t("transaction.receipt.view_button")}
                        </button>
                      )}

                      {editMode && (
                        <button
                          type="button"
                          className="btn btn-sm as-btn-soft"
                          onClick={() => receiptInputRef.current?.click()}
                        >
                          {hasReceipt || receiptFile
                            ? `✏️ ${t("transaction.receipt.edit_button")}`
                            : `📎 ${t("transaction.receipt.attach_button")}`}
                        </button>
                      )}
                    </div>

                    <input
                      ref={receiptInputRef}
                      type="file"
                      className="d-none"
                      accept="image/*,application/pdf"
                      onChange={async (e) => {
                        const f = e?.target?.files?.[0] ?? null;
                        if (!f) {
                          // User cancelled picker; keep existing selection if any.
                          return;
                        }

                        if (f.size > MAX_RECEIPT_BYTES) {
                          toggleModal(MODAL_ID.UPLOAD_ERROR, {
                            title: t("transaction.receipt.file_too_large_title"),
                            message: t("transaction.receipt.file_too_large_message", {
                              maxMb: MAX_RECEIPT_MB,
                            }),
                          });
                          return;
                        }

                        try {
                          setIsLoading?.(true);
                          const optimized = await optimizeImageToPngIfOverBytes(f, {
                            thresholdBytes: RECEIPT_OPTIMIZE_THRESHOLD_BYTES,
                            maxDimension: 1600,
                          });
                          setIsLoading?.(false);

                          if (optimized.size > MAX_RECEIPT_BYTES) {
                            toggleModal(MODAL_ID.UPLOAD_ERROR, {
                              title: t("transaction.receipt.file_too_large_title"),
                              message: t("transaction.receipt.shrink_failed_message"),
                            });
                            return;
                          }

                          setReceiptFile(optimized);
                        } catch (err) {
                          setIsLoading?.(false);
                          console.error(err);
                          toggleModal(MODAL_ID.UPLOAD_ERROR, {
                            title: t("transaction.receipt.processing_failed_title"),
                            message: t("transaction.receipt.processing_failed_message"),
                          });
                        }
                      }}
                    />

                    {receiptFile && (
                      <small className="text-muted">
                        {t("transaction.receipt.selected_prefix")} {receiptFile.name}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            )}
          <div className="mb-3 row">
          <div className="as-split-header d-flex justify-content-between align-items-center">
            <div>
              <span>
                {t("transaction.split_by")}
              </span>
              <button 
                className="btn btn-link bi-arrow-clockwise ms-2"
                onClick={splitEvenly}
                disabled={!editMode}
              />
            </div>
            <SelectTransactionType type={type} setType={setType} editMode={editMode} />
          </div>
          {type === exactType && (
            <ExactTransactionContributorList
              contributors={contributors}
              category={category}
              amount={amount}
              contributorAmountList={contributorAmountList}
              setContributorAmountList={setContributorAmountList}
              contributorAmountIsEditing={contributorAmountIsEditing}
              setContributorAmountIsEditing={setContributorAmountIsEditing}
              amountSyncVersion={amountSyncVersion}
              onAdvancedConfirm={() => setAmountSyncVersion((v) => v + 1)}
              requestEditMode={() => setEditMode(true)}
              transaction={transaction}
              newTransaction={newTransaction}
              shouldReloadRow={shouldReloadRow}
              editMode={editMode}
            />
          )}
          {type === shareType && (
            <ShareTransactionContributorList
              contributors={contributors}
              category={category}
              setContributorShareList={setContributorShareList}
              transaction={transaction}
              newTransaction={newTransaction}
              amount={amount}
              contributorShareList={contributorShareList}
              shouldReloadRow={shouldReloadRow}
              resetShare={resetShare}
              setResetShare={setResetShare}
              editMode={editMode}
            />
          )}
          <div className="modal-footer as-modal-footer">
            {formValidated && isContributionInvalid && type === exactType && (
              <p className="danger-color">{t("transaction.validation.invalid_contributor_amount_inline")}</p>
            )}
            {formValidated && isContributionInvalid && type === shareType && (
              <p className="danger-color">{t("transaction.validation.invalid_share_value_inline")}</p>
            )}
            <button
              type="button"
              className="btn as-btn-soft"
              data-bs-dismiss="modal"
            >
              {t("general.close")}
            </button>
            <button
              type="button"
              className="btn btn-primary as-btn-primary-gradient"
              onClick={handleSave}
              disabled={!editMode}
              data-bs-dismiss="modal"
            >
              {t("general.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
      {/* Receipt Preview Modal */}
      <div className="modal fade" id={receiptModalId} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content as-theme as-receipt-modal">
            <div className="modal-header">
              <h5 className="modal-title as-receipt-title">
                <span className="as-modal-icon-badge" aria-hidden="true">🧾</span>
                <AnimalEasterEggBadge seed={`${groupId}:receipt`} />
                <span>{t("transaction.receipt.preview_title")}</span>
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              {!receiptPreviewUrl && (
                <div className="text-muted">{t("transaction.receipt.loading")}</div>
              )}
              {receiptPreviewUrl && ((transaction?.receiptMimeType === "application/pdf") || (transaction?.receiptKey || "").toLowerCase().endsWith(".pdf") ? (
                <iframe
                  title={t("transaction.receipt.pdf_iframe_title")}
                  src={receiptPreviewUrl}
                  style={{ width: "100%", height: "70vh", border: "none" }}
                />
              ) : (
                <img
                  alt={t("transaction.receipt.preview_title")}
                  src={receiptPreviewUrl}
                  className="img-fluid"
                  style={{ maxHeight: "70vh", objectFit: "contain", width: "100%" }}
                />
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-sm btn-secondary" data-bs-dismiss="modal">
                {t("general.close")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <InfoModal
        modalId={receiptInfoModalId}
        icon="🔒"
        title={t("transaction.receipt.privacy_info_title")}
        body={t("transaction.receipt.privacy_info_body")}
      />
    </>
  );
}

